<template>
  <div
    class="banner-wrapper gap-2 relative max-w-(--breakpoint-xl) m-auto px-4 md:px-8 lg:px-12"
  >
    <div
      class="banner flex gap-x-12 relative items-center h-fit md:h-auto justify-between md:items-center max-md:flex-wrap max-md:gap-y-8"
      :class="compact ? 'pt-12 pb-10 md:py-12' : 'pt-12 pb-10 md:py-16'"
      :style="{
        backgroundColor: (!bgImage ? bgColor : null) || 'transparent',
      }"
    >
      <div
        v-if="bgImage || bgColor"
        class="banner_heading_bg z-1 absolute w-full h-full left-0 pointer-events-none"
        :style="{
          backgroundColor: (!bgImage ? bgColor : null) || 'transparent',
        }"
      >
        <NuxtImg
          v-if="bgImage"
          :src="bgImage"
          class="banner__image rounded-sm w-full h-full object-cover hidden md:block"
          alt="Giftcard"
          :height="'auto'"
          aspect-ratio="auto"
          sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
          preload
        />
        <NuxtImg
          v-if="bgImageMobile"
          :src="bgImageMobile"
          class="banner__image rounded-sm w-full h-full object-cover md:hidden"
          alt="Giftcard"
          :height="'auto'"
          aspect-ratio="auto"
          sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
          preload
        />
      </div>
      <div
        v-if="text || description"
        class="banner__container flex w-full md:p-0 md:[&.padded]:px-8 flex-col justify-center overflow-hidden wrap-break-word md:max-w-[572px] md:min-h-[360px]"
        :class="{ padded: padded }"
      >
        <div class="banner__heading">
          <Heading
            :level="1"
            :title="text"
            h-class="text-2xl leading-tight md:text-4xl md:leading-10 font-semibold text-teal-900"
            class="kft-heading--left"
            :test-id="testId"
          />
          <p v-if="description" class="banner__description my-4 text-stone-700">
            {{ description }}
          </p>
        </div>

        <slot name="afterHeading" />

        <slot name="button">
          <Button
            v-show="!hideButton"
            class="inline-flex self-start items-center justify-center gap-2 px-6 py-4 font-semibold"
            :link="callToActionLink"
            @click="action"
          >
            {{ actionLabel }}
            <ArrowRight :size="18" class="shrink-0" />
          </Button>
        </slot>

        <ul
          v-if="listItems.length"
          class="banner__list mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 cursor-default"
        >
          <template v-for="(item, index) in listItems" :key="index">
            <span v-if="index > 0" aria-hidden="true">&middot;</span>
            <li>{{ item }}</li>
          </template>
        </ul>
      </div>
      <slot v-if="$slots.default"></slot>
      <div
        v-else
        v-show="featuredMedia.slice(0, 1).length > 0"
        class="banner__side-gallery banner__side-gallery--flat max-sm:overflow-hidden sm:empty:hidden w-full relative max-md:overflow-hidden h-[220px] sm:h-[380px] md:h-[360px] md:w-[46%] md:shrink-0"
      >
        <div
          v-for="img in featuredMedia.slice(0, 1) || []"
          :key="img.id"
          class="side-image-container relative shrink-0 w-full h-full"
        >
          <component :is="img.url ? 'a' : 'div'" :href="img.url" class="h-full">
            <NuxtImg
              :src="String(img?.src)"
              :alt="img.alt"
              class="banner__image h-full w-full rounded-lg sm:rounded-2xl object-cover"
              :height="'auto'"
              aspect-ratio="auto"
              sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
              preload
            />
          </component>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ArrowRight } from 'lucide-vue-next'

export default {
  components: { ArrowRight },
  props: {
    height: {
      type: Number,
      default: 240,
    },
    bgColor: {
      type: String,
      default: null,
    },
    padded: {
      type: Boolean,
      default: false,
    },
    bgImage: {
      type: String,
      default: null,
    },
    bgImageMobile: {
      type: String,
      default: null,
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
    action: {
      type: Function,
      default: () => () => null,
    },
    callToActionLink: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    testId: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    featuredMedia: {
      type: Array,
      default: () => [],
    },
    listItems: {
      type: Array,
      default: () => [],
    },
    actionLabel: {
      type: String,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.banner-wrapper .banner__side-gallery:before {
  position: absolute;
  @apply md:bg-lime-200;
  display: block;
  content: none;
  width: 100%;
  height: 100%;
}
.banner-wrapper .banner__side-gallery .banner-gallery-image-text {
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
}
</style>
